// Getting a record out of a conflict (#394, #693). Sync never sends over someone
// else's save by itself (outbox.ts); the measurer chooses, on the area or in the
// held-back list:
//  - keep CRM's: this device's copy is replaced by CRM's;
//  - keep mine: this device's copy goes over CRM's, on CRM's latest etag.
// Both need signal, and both look at CRM's copy first so the screen can say
// what each choice loses (conflict-diff.ts).
import { getJobById } from './api';
import { crmCopy, crmVersionIn, mineOnCrm, type CrmVersion } from './conflict-diff';
import { forgetArea, forgetRecord, getRecord, listRecords } from './device-db';
import { db, notifyChange, queue, type DeviceRecord } from './device-store';
import { syncNow } from './outbox';

/** The job's records held back as changed elsewhere; a change CRM refused is set aside separately (refused.ts). */
export async function listHeldBack(jobId: string): Promise<DeviceRecord[]> {
  return (await listRecords(jobId)).filter((r) => r.state === 'conflict' && !r.refusedReason);
}

/** CRM's copy of each record, from one read of the job, compared with the device's copies as stored now. */
export async function loadCrmVersions(jobId: string, records: DeviceRecord[]): Promise<Map<string, CrmVersion>> {
  const job = await getJobById(jobId);
  const versions = new Map<string, CrmVersion>();
  for (const record of records) versions.set(record.id, crmVersionIn(job, (await getRecord(record.id)) ?? record));
  return versions;
}

/** CRM's copy of one held-back record, compared with the device's copy as stored now, not as a screen last read it. */
export async function loadCrmVersion(record: DeviceRecord): Promise<CrmVersion> {
  return (await loadCrmVersions(record.jobId, [record])).get(record.id)!;
}

/**
 * Replace this device's copy with CRM's, and drop anything queued for it.
 * An area's photos are untouched: ones still waiting upload as usual. A record CRM
 * removed goes from the device (an area with its photos), as a removal from elsewhere does.
 */
export async function takeCrmVersion(record: DeviceRecord, crm: CrmVersion): Promise<void> {
  if (crm.kind === 'removed') return record.table === 'area' ? forgetArea(record.id) : forgetRecord(record.id);
  const tx = (await db()).transaction(['records', 'outbox'], 'readwrite');
  for (const key of await tx.objectStore('outbox').index('byTarget').getAllKeys(record.id)) await tx.objectStore('outbox').delete(key);
  const local = (await tx.objectStore('records').get(record.id)) ?? record;
  await tx.objectStore('records').put(crmCopy(local, crm.record));
  await tx.done;
  notifyChange();
}

/** Send this device's copy over CRM's (conflict-diff.ts mineOnCrm), queue it, and sync now. */
export async function sendMineAnyway(record: DeviceRecord, crm: Extract<CrmVersion, { kind: 'changed' }>): Promise<void> {
  const tx = (await db()).transaction(['records', 'outbox'], 'readwrite');
  const local = await tx.objectStore('records').get(record.id);
  if (local) {
    await tx.objectStore('records').put(mineOnCrm(local, crm.record));
    await queue(tx, { kind: 'record', targetId: record.id, jobId: record.jobId });
  }
  await tx.done;
  notifyChange();
  void syncNow();
}
